import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import * as prog from "core/src/progression/progression.js";
import { useMatchProgress, resetMatchProgress, type MatchProgress } from "../progress";
import musicController from "../../audio/music-controller";
import type { SoundId } from "../../audio/sounds";
import GoldenEggIcon from "../GoldenEggIcon";
import LevelBadge, { levelColor } from "./LevelBadge";
import s from "./MatchXp.module.scss";

// The match's XP in its end modal (Ranked, the Play Now podium, the Royale's
// out and over cards), played out a moment after the card shows: where the XP
// came from, line by line (rounds, worms outlasted, rounds won, gaps, crashes
// caused), the quests the match finished, then the XP counts up and fills the
// ring with soft ticks. A new level stops the count for its own beat over the
// card it sits in ([data-xp-host]): the level's badge stamps in with what it
// pays; then the ring flips to the new number and the count goes on. Nothing
// until a round paid. The tally starts over when the card goes (unless `keep`).

export interface XpRun {
  xp: number;
  fromLevel: number; fromInto: number;
  parts: { id: string; n: number; xp: number }[];
  quests: { text: string; xp: number }[];
  levelEggs: number;
  dayEggs: number;
}

const START_MS = 700;    // after the card shows
const PART_GAP = 170;
const QUEST_GAP = 420;
const MOMENT_MS = 1700;  // a new level's beat (the count waits)
const TICKS = 12;

const totalAt = (level: number) => { let t = 0; for (let l = 1; l < level; l++) t += prog.xpToNext(l); return t; };
const easeIn = (x: number) => x * x * x;
const easeOut = (x: number) => 1 - Math.pow(1 - x, 3);
const easeInOut = (x: number) => (x < 0.5 ? 4 * x * x * x : 1 - Math.pow(-2 * x + 2, 3) / 2);
const reducedMotion = () => typeof window !== "undefined" && !!window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;

interface Seg { at: number; dur: number; from: number; to: number; ease: (x: number) => number }
interface Beat { at: number; kind: "part" | "quest" | "level" | "eggs" | "tick"; i: number }

// The beats of a run: when each line shows, when the count runs and where it
// stops for a new level, when the eggs come.
function timeline(run: XpRun) {
  const beats: Beat[] = [];
  let t = 0;
  run.parts.forEach((_, i) => { beats.push({ at: t, kind: "part", i }); t += PART_GAP; });
  if (run.parts.length) t += 120;
  run.quests.forEach((_, i) => { beats.push({ at: t, kind: "quest", i }); t += QUEST_GAP; });
  if (run.quests.length) t += 80;
  const from = totalAt(run.fromLevel) + run.fromInto;
  const to = from + run.xp;
  const toLevel = prog.levelOf(to).level;
  const legs: number[] = [];
  for (let l = run.fromLevel + 1; l <= toLevel; l++) legs.push(totalAt(l));
  legs.push(to);
  const segs: Seg[] = [];
  let cur = from;
  const countStart = t;
  legs.forEach((end, k) => {
    const last = k === legs.length - 1;
    const dur = Math.max(320, Math.min(1100, 300 + 900 * ((end - cur) / Math.max(1, run.xp))));
    segs.push({ at: t, dur, from: cur, to: end, ease: legs.length === 1 ? easeOut : k === 0 ? easeIn : last ? easeOut : easeInOut });
    t += dur;
    if (!last) { beats.push({ at: t, kind: "level", i: run.fromLevel + k + 1 }); t += MOMENT_MS; }
    cur = end;
  });
  const countEnd = t;
  // a tick each time the count passes a twelfth of the XP
  for (let k = 0; k < TICKS && run.xp > 0; k++) {
    const v = from + (run.xp * (k + 1)) / TICKS;
    const seg = segs.find((g) => v <= g.to + 1e-6) || segs[segs.length - 1];
    let lo = 0, hi = 1;
    for (let j = 0; j < 18; j++) { const m = (lo + hi) / 2; if (seg.from + (seg.to - seg.from) * seg.ease(m) < v) lo = m; else hi = m; }
    beats.push({ at: seg.at + hi * seg.dur, kind: "tick", i: k });
  }
  if (run.levelEggs + run.dayEggs > 0) { beats.push({ at: t + 150, kind: "eggs", i: 0 }); t += 500; }
  beats.sort((a, b) => a.at - b.at);
  return { beats, segs, from, countStart, countEnd, end: t + 200 };
}

function valueAt(segs: Seg[], from: number, ms: number): number {
  let v = from;
  for (const g of segs) {
    if (ms <= g.at) break;
    v = g.from + (g.to - g.from) * g.ease(Math.min(1, (ms - g.at) / g.dur));
  }
  return v;
}

const SOUND: Record<Beat["kind"], (b: Beat) => string> = {
  part: () => "next-round-tick",
  quest: () => "quest-done",
  level: () => "level-up",
  eggs: () => "egg-coin",
  tick: (b) => `xp-tick-${b.i}`,
};

// Plays a run when `playKey` changes (0: not yet, the start shows); new data
// for the same key jumps to its end. `sound` false keeps quiet.
function useRun(run: XpRun, playKey: number, sound: boolean) {
  const tl = useMemo(() => timeline(run), [run]);
  const still = useRef(reducedMotion()).current;
  const [ms, setMs] = useState(still ? tl.end : 0);
  const played = useRef(0);
  useEffect(() => {
    if (still) { setMs(tl.end); return; }
    if (!playKey) { setMs(0); return; }
    if (playKey === played.current) { setMs(tl.end); return; }
    played.current = playKey;
    let raf = 0, last = -1;
    const t0 = performance.now();
    const frame = (now: number) => {
      const e = now - t0;
      if (sound) for (const b of tl.beats) if (b.at > last && b.at <= e) { try { musicController.play(SOUND[b.kind](b) as SoundId); } catch { /* no audio */ } }
      last = e;
      setMs(Math.min(e, tl.end));
      if (e < tl.end) raf = requestAnimationFrame(frame);
    };
    raf = requestAnimationFrame(frame);
    return () => cancelAnimationFrame(raf);
  }, [playKey, tl, sound, still]);
  const total = valueAt(tl.segs, tl.from, ms);
  const lv = prog.levelOf(Math.round(total));
  const levelBeat = tl.beats.filter((b) => b.kind === "level" && b.at <= ms).pop();
  return {
    done: ms >= tl.end,
    level: lv.level, into: lv.into, need: lv.need,
    gained: ms >= tl.countStart ? Math.round(total - tl.from) : 0,
    shown: (kind: Beat["kind"], i: number) => tl.beats.some((b) => b.kind === kind && b.i === i && b.at <= ms),
    sinceLevel: levelBeat ? ms - levelBeat.at : Infinity,
    eggsIn: tl.beats.some((b) => b.kind === "eggs" && b.at <= ms),
  };
}

// The ring with the level in it; a new level flashes it, sends a ring out and
// flips the number up.
function FlipRing({ level, into, need, flash }: { level: number; into: number; need: number; flash: boolean }) {
  const [prev, setPrev] = useState<number | null>(null);
  const seen = useRef(level);
  useEffect(() => {
    if (level === seen.current) return;
    setPrev(seen.current);
    seen.current = level;
    const id = window.setTimeout(() => setPrev(null), 420);
    return () => window.clearTimeout(id);
  }, [level]);
  const [gid] = useState(() => "mx" + Math.random().toString(36).slice(2, 8));
  const pct = Math.max(0, Math.min(1, into / Math.max(1, need)));
  const r = 45.5, c = 2 * Math.PI * r;
  return (
    <span className={`${s.ring} ${flash ? s.flash : ""}`}>
      <svg viewBox="0 0 100 100" aria-hidden="true">
        <defs><linearGradient id={gid} x1="0" y1="0" x2="1" y2="1"><stop offset="0" stopColor="#28c4d4" /><stop offset="1" stopColor="#55d16a" /></linearGradient></defs>
        <circle cx="50" cy="50" r={r} stroke="rgba(255,255,255,0.1)" strokeWidth="9" fill="none" />
        {pct > 0 && <circle className={s.ringArc} cx="50" cy="50" r={r} stroke={`url(#${gid})`} strokeWidth="9" fill="none" strokeDasharray={`${c * pct} ${c}`} transform="rotate(-90 50 50)" />}
      </svg>
      {flash && <span className={s.burst} aria-hidden="true" />}
      <span className={s.num}>
        {prev != null && <b key={"o" + prev} className={s.numOut}>{prev}</b>}
        <b key={level} className={prev != null ? s.numIn : ""}>{level}</b>
      </span>
    </span>
  );
}

const Check = () => <svg viewBox="0 0 16 16" aria-hidden="true"><path d="M3 8.5l3.2 3L13 4.5" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round" /></svg>;
const PART_NAME: Record<string, string> = { played: "Rounds", outlasted: "Players beaten", won: "Rounds won", gaps: "Gaps", crashes: "Crashes caused" };

// One run, played: the lab feeds its own (/debug/progress-lab).
export function MatchXpRun({ run, playKey, sound = true, className }: { run: XpRun; playKey: number; sound?: boolean; className?: string }) {
  const r = useRun(run, playKey, sound);
  const wrap = useRef<HTMLDivElement | null>(null);
  const [host, setHost] = useState<HTMLElement | null>(null);
  useLayoutEffect(() => { setHost(wrap.current?.closest<HTMLElement>("[data-xp-host]") ?? null); }, []);
  const eggs = run.levelEggs + run.dayEggs;
  const leveled = r.level > run.fromLevel;
  const moment = r.sinceLevel < MOMENT_MS;
  const momentEl = moment ? (
    <div className={s.moment} aria-hidden="true">
      <span className={s.mGlow} />
      <span className={s.mBadge} style={{ "--lc": levelColor(r.level) } as React.CSSProperties}><LevelBadge level={r.level} size={84} /></span>
      <b className={s.mTitle}>Level {r.level}</b>
      {run.levelEggs > 0 && prog.LEVEL_EGGS.some((l: { level: number }) => l.level === r.level) && <span className={s.mEggs}>+{run.levelEggs}<GoldenEggIcon size={16} /></span>}
    </div>
  ) : null;
  return (
    <div ref={wrap} className={`${s.wrap} ${className || ""}`}>
      {run.parts.length > 0 && (
        <div className={s.parts}>
          {run.parts.map((p, i) => (
            <span key={p.id} className={`${s.part} ${r.shown("part", i) ? s.in : ""}`}>
              <span>{PART_NAME[p.id] || p.id}{p.id !== "won" || p.n > 1 ? <i>×{p.n}</i> : null}</span><em>+{p.xp}</em>
            </span>
          ))}
        </div>
      )}
      <div className={s.row}>
        <FlipRing level={r.level} into={r.into} need={r.need} flash={r.sinceLevel < 650} />
        <div className={s.main}>
          <div className={s.top}>
            <b>Level {r.level}</b>
            {leveled && <em key={r.level} className={s.up}>Level up</em>}
            <span className={s.gain}>+{r.gained} XP</span>
          </div>
          <span className={s.bar}><i style={{ width: `${(r.into / Math.max(1, r.need)) * 100}%` }} /></span>
          {(run.quests.length > 0 || eggs > 0) && (
            <div className={s.quests}>
              {run.quests.map((q, i) => (
                <span key={i} className={`${s.quest} ${r.shown("quest", i) ? s.in : ""}`}><span className={s.qCheck}><Check /></span>{q.text}<em>+{q.xp}</em></span>
              ))}
              {eggs > 0 && <span className={`${s.eggs} ${r.eggsIn ? s.in : ""}`}>+{eggs}<GoldenEggIcon size={14} /></span>}
            </div>
          )}
        </div>
      </div>
      {momentEl && (host ? createPortal(momentEl, host) : momentEl)}
    </div>
  );
}

const toRun = (m: MatchProgress): XpRun => ({ xp: m.xp, fromLevel: m.fromLevel, fromInto: m.fromInto, parts: m.parts, quests: m.quests, levelEggs: m.levelEggs, dayEggs: m.dayEggs });

export default function MatchXp({ keep, className }: { keep?: boolean; className?: string }) {
  const m = useMatchProgress();
  const live = useMemo(() => (m ? toRun(m) : null), [m]);
  // It plays once, with what has come by then; a round that lands while it
  // plays shows at its end.
  const [playKey, setPlayKey] = useState(0);
  const [frozen, setFrozen] = useState<XpRun | null>(null);
  useEffect(() => {
    const id = window.setTimeout(() => setPlayKey(1), START_MS);
    return () => window.clearTimeout(id);
  }, []);
  useEffect(() => {
    if (playKey !== 1 || !live) return;
    setFrozen(live);
    const id = window.setTimeout(() => setFrozen(null), timeline(live).end + 100);
    return () => window.clearTimeout(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [playKey]);
  useEffect(() => () => { if (!keep) resetMatchProgress(); }, [keep]);
  const run = frozen ?? live;
  if (!run || run.xp <= 0) return null;
  return <MatchXpRun run={run} playKey={playKey} className={className} />;
}
